import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Timeline } from 'antd';
import {
  EditOutlined,
  ThunderboltOutlined,
  SaveOutlined,
  SwapOutlined,
  BgColorsOutlined,
  MailOutlined,
  PlusOutlined,
  FormOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';

import TimelineStepText from '../common/containers/TimelineStepText';

import { getCardEvents, openCardDrawer, openShortListDrawer } from './state/boards.actions';
import { DEFAULT_TIMELINE_COLOR, formatTimelineCardColor } from './boards.helpers';
import BoardColorOption from './BoardColorOption';
import { getIdMap } from '../helpers/helpers';

import Permissions from '../auth/Permissions';
import FormUpdateStep from '../forms/CompletedForms/FormUpdateStep';
import FormTimelineCardLink from '../forms/CompletedForms/FormTimelineCardLink';

const PROP_MAP = {
  create: {
    title: 'Card Created',
    dot: <SaveOutlined />,
  },
  edit: {
    title: 'Card Edited',
    dot: <EditOutlined />,
  },
  workflowCreate: {
    title: 'Card Created from Workflow',
    dot: <ThunderboltOutlined />,
  },
  statusChange: {
    title: 'Card Status Changed',
    dot: <SwapOutlined />,
  },
  colorChange: {
    title: 'Card Color Changed',
    dot: <BgColorsOutlined />,
  },
  workflowUpdate: {
    title: 'Card Updated from Workflow',
    dot: <FormOutlined />,
  },
  workflowChildCreate: {
    title: 'Child Card Created from Workflow',
    dot: <PlusOutlined />,
  },
};

const bold = (text) => <span style={{ fontFamily: 'roboto-regular' }}>{text} </span>;
const Ontraccr = bold('Ontraccr');
const boardPrefix = (board) => (board ? `${board}:` : '');
const statusTitle = (board, status) => bold(`${boardPrefix(board)}${status}`);
const colorTitle = (color, colorContext) => (
  <span className="board-card-timeline-color">
    <BoardColorOption
      color={color || DEFAULT_TIMELINE_COLOR}
      label={formatTimelineCardColor(color, colorContext)}
    />
  </span>
);

export default function BoardCardTimeline({
  active,
  cardId,
  canEdit,
}) {
  const dispatch = useDispatch();
  const history = useHistory();
  const cardEvents = useSelector((state) => state.boards.cardEvents);
  const selectedBoard = useSelector((state) => state.boards.selectedBoard);
  const boardDetailsMap = useSelector((state) => state.boards.boardDetailsMap);
  const users = useSelector((state) => state.users.users);

  const userMap = useMemo(() => getIdMap(users), [users]);

  const statusMap = useMemo(() => {
    const { statuses = [] } = selectedBoard ?? {};
    return getIdMap(statuses);
  }, [selectedBoard]);

  const onShortList = (shortListCardId) => {
    dispatch(openShortListDrawer({
      selectedCardId: shortListCardId,
    }));
  };

  const onCardLinkClick = (payload) => {
    const { cardId: linkCardId, boardId } = payload;
    if (linkCardId && boardId) {
      history.push('/boards', { boardId, cardId: linkCardId, activeTab: 'details' });
      dispatch(openCardDrawer({
        selectedCardId: linkCardId,
        onShortList,
      }));
    }
  };

  const eventList = useMemo(() => {
    const {
      [cardId]: { events: ourList = [] } = {},
    } = cardEvents;
    const colorContext = { selectedBoard, boardDetailsMap };
    return ourList.map((event) => {
      const {
        id: eventId,
        type,
        timestamp,
        userId,
        metadata,
        subType,
      } = event;
      const props = PROP_MAP[type] ?? {};
      let { [userId]: { name: userName } = {} } = userMap;
      let text;
      if (Permissions.id === userId) userName = 'You';

      switch (type) {
        case 'create': {
          const {
            status, board,
          } = metadata;

          const statusText = status ? <span> with status {statusTitle(board, status)}.</span> : '.';
          if (userName) {
            text = (
              <span>
                {bold(userName)} created the card{statusText}
              </span>
            );
          } else {
            text = (
              <span>
                The card was created{statusText}
              </span>
            );
          }
          break;
        }
        case 'edit': {
          const { isOffline } = metadata ?? {};
          const noUserText = `The card was edited${isOffline ? ' offline' : ''}.`;
          text = userName ? <span>{bold(userName)} edited the card{isOffline ? ' offline' : ''}.</span> : noUserText;
          break;
        }
        case 'workflowCreate': {
          const {
            status,
            board,
            triggerName,
            parentCard,
          } = metadata;
          const {
            cardTitle: parentCardTitle,
            cardId: parentCardId,
            boardId: parentCardBoardId,
          } = parentCard ?? {};
          const triggerText = triggerName
            ? <span>used the {bold(triggerName)} workflow to create</span>
            : 'created';
          const statusText = status
            ? <span> with status {statusTitle(board, status)}</span>
            : '';
          const parentCardText = parentCard ? (
            <span>
              {' '}triggered by card
              <FormTimelineCardLink
                cardTitle={parentCardTitle}
                cardId={parentCardId}
                boardId={parentCardBoardId}
                onCardLinkClick={onCardLinkClick}
              />
            </span>
          ) : '';
          text = (
            <span>
              {Ontraccr} {triggerText} the card{statusText}{parentCardText}
            </span>
          );
          break;
        }
        case 'workflowUpdate': {
          const {
            status,
            board,
            triggerName,
            oldData,
            newData,
          } = metadata;
          const triggerText = triggerName
            ? <span>used the {bold(triggerName)} workflow to update</span>
            : 'updated';
          const statusText = status ? <span> with status {statusTitle(board, status)}</span> : '';
          text = (
            <>
              <span>{Ontraccr} {triggerText} the card{statusText}</span>
              <FormUpdateStep
                oldData={{ sections: oldData }}
                newData={{ sections: newData }}
              />
            </>
          );
          break;
        }
        case 'statusChange': {
          const {
            board,
            sourceStatus,
            sourceBoard = board,
            destinationStatus,
            destinationBoard = board,
            triggerName,
            isOffline,
          } = metadata ?? {};
          const destText = destinationStatus
            ? <span> to {statusTitle(destinationBoard, destinationStatus)}</span>
            : '.';

          const statusText = sourceStatus
            ? <span> from {statusTitle(sourceBoard, sourceStatus)}{destText}</span>
            : destText;

          const offlineText = isOffline ? ' offline' : '';

          if (triggerName) {
            // Workflow triggered the status change
            text = (
              <span>
                {Ontraccr} used the {bold(triggerName)} workflow to move the card{statusText}
              </span>
            );
          } else if (userName) {
            text = (
              <span>{bold(userName)} moved the card{offlineText}{statusText}
              </span>
            );
          } else {
            text = (
              <span>The card was moved{offlineText}{statusText}
              </span>
            );
          }
          break;
        }
        case 'colorChange': {
          const {
            previousColor,
            newColor,
          } = metadata ?? {};
          const fromText = colorTitle(previousColor, colorContext);
          const toText = colorTitle(newColor, colorContext);

          text = (
            <span>
              {bold(userName)} changed the card color from {fromText} to {toText}.
            </span>
          );
          break;
        }
        case 'notInStatus':
        case 'statusTime': {
          const {
            emails,
            color,
            previousColor,
            triggerName,
          } = metadata ?? {};

          switch (subType) {
            case 'changeCardColor':
              text = previousColor ? (
                <span>
                  {Ontraccr} used the {bold(triggerName)} workflow to change the
                  {' '}card color from {colorTitle(previousColor, colorContext)}
                  {' '}to {colorTitle(color, colorContext)}.
                </span>
              ) : (
                <span>
                  {Ontraccr} used the {bold(triggerName)} workflow to change the
                  {' '}card color to: {colorTitle(color, colorContext)}
                </span>
              );
              props.title = 'Workflow Trigger - Card Color Changed';
              props.dot = <BgColorsOutlined />;
              break;
            case 'sendEmail':
              text = <span>{Ontraccr} used the {bold(triggerName)} workflow to send an email to: {emails.join(', ')}</span>;
              props.title = 'Workflow Trigger - Email Sent';
              props.dot = <MailOutlined />;
              break;
            case 'createTask':
              text = <span>{Ontraccr} used the {bold(triggerName)} workflow to create a task.</span>;
              props.title = 'Workflow Trigger - Task Created';
              props.dot = <PlusOutlined />;
              break;
            default:
              break;
          }
          break;
        }
        case 'workflowCustomerProject': {
          const { customerId, projectId } = metadata;

          if (customerId && projectId) {
            props.title = 'Workflow Trigger - Project & Customer Created';
          } else if (customerId) {
            props.title = 'Workflow Trigger - Customer Created';
          } else {
            props.title = 'Workflow Trigger - Project Created';
          }

          switch (subType) {
            case 'createCard':
              text = <span>{Ontraccr} used the create customer and/or project when a card is created workflow.</span>;
              props.dot = <ThunderboltOutlined />;
              break;
            case 'statusChange': {
              text = <span>{Ontraccr} used the create customer and/or project on status change workflow.</span>;
              props.dot = <SwapOutlined />;
              break;
            }
            default:
              break;
          }

          break;
        }
        case 'workflowChildCreate': {
          const {
            triggerName,
            childCard,
          } = metadata;
          const {
            status,
            board,
            cardTitle: childCardTitle,
            id: childCardId,
            boardId: childCardBoardId,
          } = childCard ?? {};

          text = (
            <span>
              {Ontraccr} ran the {bold(triggerName)} workflow,
              which triggered a child form that created a board card
              <FormTimelineCardLink
                cardTitle={childCardTitle}
                cardId={childCardId}
                boardId={childCardBoardId}
                onCardLinkClick={onCardLinkClick}
              /> with status {statusTitle(board, status)}.
            </span>
          );
          break;
        }
        default:
          // Shouldnt reach this
          props.title = 'Unknown Event Type';
          text = <span>Unknown event type: {type}</span>;
          break;
      }

      return (
        <Timeline.Item
          color="black"
          key={eventId}
          {...props}
        >
          <TimelineStepText
            {...props}
            timestamp={timestamp}
            text={text}
          />
        </Timeline.Item>
      );
    });
  }, [cardId, cardEvents, userMap, statusMap, selectedBoard, boardDetailsMap]);

  useEffect(() => {
    if (active && cardId) dispatch(getCardEvents(cardId));
  }, [active, cardId]);

  return (
    <div className={`board-card-timeline-container-${canEdit ? 'write' : 'read'}`}>
      <Timeline>
        {eventList}
      </Timeline>
    </div>
  );
}

BoardCardTimeline.propTypes = {
  active: PropTypes.bool,
  cardId: PropTypes.string,
  canEdit: PropTypes.bool,
};

BoardCardTimeline.defaultProps = {
  active: false,
  cardId: null,
  canEdit: false,
};
